import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useClientProfileStore } from '../store/clientProfileStore';
import { Cog6ToothIcon, EyeIcon, XMarkIcon, CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { NudgeIcon } from './common/NudgeLogo';
import '../styles/portal-v2.css';

const ClientPortalLayout: React.FC = () => {
  const { user, isImpersonating, exitImpersonation } = useAuthStore();
  const { profiles, activeClientId, fetchProfiles, setActive, reset } = useClientProfileStore();
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const initials = user ? `${user.firstName[0]}${user.lastName[0]}` : '?';

  // Charge les profils accessibles (client principal + comptes additionnels) au montage.
  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  // Fermer le menu au clic extérieur.
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  const handleExitImpersonation = () => {
    reset();
    exitImpersonation();
    navigate('/settings');
  };

  const multi = profiles.length > 1;
  const activeProfile = profiles.find(p => p.id === activeClientId);
  // Avatar/nom du header = profil actif si dispo, sinon fallback sur les infos user.
  const headerPic = activeProfile?.profilePicUrl || user?.clientProfilePicUrl || null;
  const clientInitial = activeProfile?.name?.[0]?.toUpperCase();

  const handleSwitch = (clientId: string) => {
    setActive(clientId);
    setMenuOpen(false);
  };

  // Clic sur l'icône : ouvre le switcher si multi-comptes, sinon va direct au compte.
  const handleAccountClick = () => {
    if (multi) setMenuOpen(o => !o);
    else navigate('/client-portal/account');
  };

  const renderAvatar = (pic: string | null, fallback: string, size: string) =>
    pic ? (
      <img src={pic} alt={fallback} className={`${size} rounded-full object-cover border border-neutral-200 dark:border-neutral-700`} />
    ) : (
      <div className={`${size} rounded-full flex items-center justify-center`} style={{ backgroundColor: 'var(--ev-accent-soft)' }}>
        <span className="text-xs font-bold" style={{ color: 'var(--ev-accent)' }}>{fallback}</span>
      </div>
    );

  return (
    <div className="nudge-portal min-h-screen pwa-safe-top" style={{ backgroundColor: 'var(--ev-base)' }}>
      {/* Impersonation banner */}
      {isImpersonating && (
        <div className="px-3 sm:px-4 py-2 flex items-center justify-between gap-2 sticky top-0 z-40" style={{
          backgroundColor: 'var(--ev-warn-soft)',
          borderBottom: '1px solid var(--ev-warn)',
          color: 'var(--ev-warn)'
        }}>
          <div className="flex items-center gap-2 min-w-0">
            <EyeIcon className="w-4 h-4 flex-shrink-0" />
            <span className="text-xs sm:text-sm font-medium truncate">
              Connecte en tant que {user?.firstName} {user?.lastName}
            </span>
          </div>
          <button
            onClick={handleExitImpersonation}
            className="flex items-center gap-1.5 px-3 py-1 text-white text-xs sm:text-sm font-medium rounded-lg transition-colors flex-shrink-0"
            style={{ backgroundColor: 'var(--ev-warn)', cursor: 'pointer' }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
          >
            <XMarkIcon className="w-3.5 h-3.5" />
            <span>Quitter</span>
          </button>
        </div>
      )}

      {/* Header */}
      <header className={`backdrop-blur-xl ${isImpersonating ? '' : 'sticky top-0'} z-30`} style={{
        backgroundColor: 'var(--ev-panel)',
        borderBottom: '1px solid var(--ev-line)'
      }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            {/* Brand */}
            <div className="flex items-center gap-2.5">
              <div style={{ color: 'var(--ev-accent)' }}>
                <NudgeIcon className="w-7 h-7" />
              </div>
              <span className="text-lg font-bold tracking-tight" style={{ color: 'var(--ev-accent)' }}>
                NUDGE
              </span>
            </div>

            {/* Accès au compte + switch de profil (si plusieurs comptes accessibles).
                Clic sur l'icône : switcher si multi-comptes, sinon "Mon compte". */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={handleAccountClick}
                className="flex items-center gap-2 rounded-full pl-2 pr-1 py-1 transition-colors"
                title={multi ? 'Changer de compte' : 'Mon compte'}
                style={{ color: 'var(--ev-muted)' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--ev-raised)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                {multi
                  ? <ChevronDownIcon className={`w-4 h-4 transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
                  : <Cog6ToothIcon className="w-5 h-5" />}
                {renderAvatar(headerPic, (clientInitial || initials), 'w-8 h-8')}
              </button>

              {multi && menuOpen && (
                <div
                  className="absolute right-0 mt-2 w-64 rounded-2xl overflow-hidden z-50 py-1.5"
                  style={{ backgroundColor: 'var(--ev-panel)', boxShadow: 'var(--ev-shadow), inset 0 0 0 1px var(--ev-line)' }}
                >
                  <div className="px-3 pt-1.5 pb-1 text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--ev-faint)' }}>
                    Comptes
                  </div>
                  {profiles.map(p => {
                    const active = p.id === activeClientId;
                    return (
                      <button
                        key={p.id}
                        onClick={() => handleSwitch(p.id)}
                        className="w-full flex items-center gap-2.5 px-3 py-2 transition-colors text-left"
                        style={{ color: 'var(--ev-text)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--ev-raised)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                      >
                        {renderAvatar(p.profilePicUrl, (p.name[0] || '?').toUpperCase(), 'w-7 h-7')}
                        <span className="flex-1 text-sm font-medium truncate">{p.name}</span>
                        {active && <CheckIcon className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--ev-accent)' }} />}
                      </button>
                    );
                  })}
                  <div className="my-1.5 mx-3 border-t" style={{ borderColor: 'var(--ev-line)' }} />
                  <button
                    onClick={() => { setMenuOpen(false); navigate('/client-portal/account'); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 transition-colors text-left"
                    style={{ color: 'var(--ev-muted)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--ev-raised)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <Cog6ToothIcon className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm font-medium">Mon compte</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Content — hauteur fiabilisée en inline (les classes Tailwind arbitraires calc() ne sont pas
          toujours générées → le conteneur débordait le viewport et la barre du bas mangeait la liste).
          dvh pour gérer la barre d'adresse mobile ; on retranche header (3.5rem) + bannière (2.5rem). */}
      <main
        className="overflow-hidden"
        style={{ height: isImpersonating ? 'calc(100dvh - 3.5rem - 2.5rem)' : 'calc(100dvh - 3.5rem)' }}
      >
        <Outlet />
      </main>
    </div>
  );
};

export default ClientPortalLayout;
